import { useState, useEffect, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Shield, Loader2, ChevronDown } from 'lucide-react';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

/* ─── Constantes ──────────────────────────────────────────── */

function getAccountTypes() {
    return [
        { value: 'companies', label: i18n.t('account:entreprises') },
        { value: 'individuals', label: i18n.t('account:particuliers') },
    ];
}

// Super Admin (role `admin`) non affiché — il a tous les droits.
// Mapping : label front → nom du rôle API
function getCompanyRoles() {
    return [
        { key: 'admin_col', label: i18n.t('account:admin'), apiRole: 'owner' },
        { key: 'manager_col', label: i18n.t('account:manager'), apiRole: 'head_department' },
        { key: 'member_col', label: i18n.t('account:membre'), apiRole: 'employee' },
    ];
}

function getIndividualRole() {
    return [{ key: 'user_col', label: i18n.t('account:particuliers'), apiRole: 'user' }];
}

// apiCode: null  → disabled (permission trop granulaire, pas d'équivalent direct en API)
// apiScope       → 'SELF' | 'DESCENDANTS' (défaut 'SELF')
function getCompanyPermissionGroups() {
    return [
        {
            theme: 'Photos',
            permissions: [
                {
                    id: 'capture_photos',
                    label: i18n.t('account:capturerDesPhotos'),
                    apiCode: 'CAPTURE_CERTIFIED_PHOTOS',
                    apiScope: 'SELF',
                },
                {
                    id: 'move_photo',
                    label: i18n.t('account:deplacerUnePhoto'),
                    apiCode: 'MOVE_CERTIFIED_PHOTOS',
                    apiScope: 'SELF',
                },
            ],
        },
        {
            theme: i18n.t('account:procedures'),
            permissions: [
                {
                    id: 'manage_procedures',
                    label: i18n.t('account:gererDesProceduresCreationPartageArchivage'),
                    apiCode: 'UPDATE_PROCEDURES',
                    apiScope: 'SELF',
                },
                { id: 'create_procedures', label: i18n.t('account:creerDesProcedures'), apiCode: 'CREATE_PROCEDURES', apiScope: 'SELF' },
                {
                    id: 'archive_procedures',
                    label: i18n.t('account:archiverDesProcedures'),
                    apiCode: 'ARCHIVE_PROCEDURES',
                    apiScope: 'SELF',
                },
                {
                    id: 'complete_procedure',
                    label: i18n.t('account:completerUneProcedure'),
                    apiCode: 'UPDATE_PROCEDURE_REPORTS',
                    apiScope: 'SELF',
                },
                { id: 'move_report', label: i18n.t('account:deplacerUnRapport'), apiCode: 'MOVE_PROCEDURE_REPORTS', apiScope: 'SELF' },
            ],
        },
        {
            theme: 'Dossiers',
            permissions: [
                {
                    id: 'view_all_folders',
                    label: i18n.t('account:consulterTousLesDossiers'),
                    apiCode: 'LIST_FOLDERS',
                    apiScope: 'SELF',
                },
                {
                    id: 'view_subaccount_folders',
                    label: i18n.t('account:consulterLesDossiersDesSousComptes'),
                    apiCode: 'LIST_FOLDERS',
                    apiScope: 'DESCENDANTS',
                },
                { id: 'add_folders', label: i18n.t('account:ajouterDesDossiers'), apiCode: 'CREATE_FOLDERS', apiScope: 'SELF' },
                { id: 'edit_folders', label: i18n.t('account:modifierDesDossiers'), apiCode: 'UPDATE_FOLDERS', apiScope: 'SELF' },
                {
                    id: 'edit_folder_managers',
                    label: i18n.t('account:modifierLesResponsablesDeDossier'),
                    apiCode: 'UPDATE_FOLDER_MANAGERS',
                    apiScope: 'SELF',
                },
                { id: 'move_folder', label: i18n.t('account:deplacerUnDossier'), apiCode: 'MOVE_FOLDERS', apiScope: 'SELF' },
                { id: 'archive_folders', label: i18n.t('account:archiverDesDossiers'), apiCode: 'ARCHIVE_FOLDERS', apiScope: 'SELF' },
            ],
        },
        {
            theme: 'Jetons',
            permissions: [
                {
                    id: 'access_token_purchase',
                    label: i18n.t('account:accederALaPageAchatDeJetons'),
                    apiCode: 'VIEW_TOKENS',
                    apiScope: 'SELF',
                },
                {
                    id: 'view_company_balance',
                    label: i18n.t('account:afficherLeSoldeDeJetonsDeLEntreprise'),
                    apiCode: 'VIEW_COMPANY_TOKENS',
                    apiScope: 'SELF',
                },
                {
                    id: 'send_tokens_subaccount',
                    label: i18n.t('account:envoyerDesJetonsAUnSousCompte'),
                    apiCode: 'SEND_TOKENS',
                    apiScope: 'DESCENDANTS',
                },
            ],
        },
        {
            theme: i18n.t('account:equipe'),
            permissions: [
                { id: 'view_team', label: i18n.t('account:afficherLEquipe'), apiCode: 'LIST_USERS', apiScope: 'SELF' },
                {
                    id: 'add_team_members',
                    label: i18n.t('account:ajouterDesCollaborateursALEquipe'),
                    apiCode: 'CREATE_COMPANY_MEMBERS',
                    apiScope: 'SELF',
                },
                {
                    id: 'edit_team_members',
                    label: i18n.t('account:modifierDesCollaborateursDeLEquipe'),
                    apiCode: 'UPDATE_COMPANY_MEMBERS',
                    apiScope: 'SELF',
                },
                {
                    id: 'delete_team_members',
                    label: i18n.t('account:supprimerDesCollaborateursDeLEquipe'),
                    apiCode: 'ARCHIVE_COMPANY_MEMBERS',
                    apiScope: 'SELF',
                },
            ],
        },
        {
            theme: 'Sous-comptes',
            permissions: [
                {
                    id: 'add_subaccounts',
                    label: i18n.t('account:ajouterDesSousComptes'),
                    apiCode: 'CREATE_SUBSIDIARIES',
                    apiScope: 'SELF',
                },
                {
                    id: 'edit_subaccount_permissions',
                    label: i18n.t('account:modifierLesAutorisationsDesSousComptes'),
                    apiCode: 'UPDATE_SUBSIDIARY_PERMISSIONS',
                    apiScope: 'DESCENDANTS',
                },
                {
                    id: 'edit_subaccount_info',
                    label: i18n.t('account:modifierLesInformationsDesSousComptes'),
                    apiCode: 'UPDATE_SUBSIDIARIES',
                    apiScope: 'DESCENDANTS',
                },
            ],
        },
        {
            theme: 'Gestion des appareils',
            permissions: [
                {
                    id: 'access_device_management',
                    label: i18n.t('account:accederALaPageGestionDesAppareils'),
                    apiCode: 'LIST_DEVICES',
                    apiScope: 'SELF',
                },
            ],
        },
    ];
}

function getIndividualPermissionGroups() {
    return [
        {
            theme: 'Photos',
            permissions: [
                {
                    id: 'ind_capture_photos',
                    label: i18n.t('account:capturerDesPhotos'),
                    apiCode: 'CAPTURE_CERTIFIED_PHOTOS',
                    apiScope: 'SELF',
                },
                {
                    id: 'ind_move_photo',
                    label: i18n.t('account:deplacerUnePhoto'),
                    apiCode: 'MOVE_CERTIFIED_PHOTOS',
                    apiScope: 'SELF',
                },
            ],
        },
        {
            theme: i18n.t('account:procedures'),
            permissions: [
                {
                    id: 'ind_complete_procedure',
                    label: i18n.t('account:completerUneProcedure'),
                    apiCode: 'UPDATE_PROCEDURE_REPORTS',
                    apiScope: 'SELF',
                },
                {
                    id: 'ind_move_report',
                    label: i18n.t('account:deplacerUnRapport'),
                    apiCode: 'MOVE_PROCEDURE_REPORTS',
                    apiScope: 'SELF',
                },
            ],
        },
        {
            theme: 'Dossiers',
            permissions: [
                { id: 'ind_add_folders', label: i18n.t('account:ajouterDesDossiers'), apiCode: 'CREATE_FOLDERS', apiScope: 'SELF' },
                { id: 'ind_edit_folders', label: i18n.t('account:modifierDesDossiers'), apiCode: 'UPDATE_FOLDERS', apiScope: 'SELF' },
                {
                    id: 'ind_edit_folder_managers',
                    label: i18n.t('account:modifierLesResponsablesDeDossier'),
                    apiCode: 'UPDATE_FOLDER_MANAGERS',
                    apiScope: 'SELF',
                },
                { id: 'ind_move_folder', label: i18n.t('account:deplacerUnDossier'), apiCode: 'MOVE_FOLDERS', apiScope: 'SELF' },
                { id: 'ind_archive_folders', label: i18n.t('account:archiverDesDossiers'), apiCode: 'ARCHIVE_FOLDERS', apiScope: 'SELF' },
            ],
        },
        {
            theme: 'Jetons',
            permissions: [
                {
                    id: 'ind_access_token_purchase',
                    label: i18n.t('account:accederALaPageAchatDeJetons'),
                    apiCode: 'VIEW_TOKENS',
                    apiScope: 'SELF',
                },
            ],
        },
    ];
}

/* ─── Helpers ─────────────────────────────────────────────── */

function getPermValue(perm, apiRoleName, matrix) {
    if (!perm.apiCode) return false;
    return matrix[apiRoleName]?.[perm.apiCode]?.[perm.apiScope ?? 'SELF'] ?? false;
}

function makeSavingKey(perm, apiRoleName) {
    return `${apiRoleName}:${perm.apiCode}:${perm.apiScope ?? 'SELF'}`;
}

function isGroupAllChecked(group, apiRoleName, matrix) {
    const enabledPerms = group.permissions.filter((p) => p.apiCode);
    if (enabledPerms.length === 0) return false;
    return enabledPerms.every((p) => getPermValue(p, apiRoleName, matrix));
}

/* ─── Table groupée par thème (accordion) ─────────────────── */

function GroupedPermissionsTable({ groups, roles, matrix, savingKey, onToggle, onGroupToggle, savingGroupKey }) {
    const { t } = useTranslation();
    const colCount = roles.length;
    const desktopGridCols =
        {
            1: 'grid-cols-[1fr_140px_32px]',
            2: 'grid-cols-[1fr_repeat(2,_100px)_32px]',
            3: 'grid-cols-[1fr_repeat(3,_100px)_32px]',
            4: 'grid-cols-[1fr_repeat(4,_100px)_32px]',
            5: 'grid-cols-[1fr_repeat(5,_100px)_32px]',
        }[colCount] ?? 'grid-cols-[1fr_repeat(4,_100px)_32px]';

    const allThemes = groups.map((g) => g.theme);

    return (
        <div className="space-y-0">
            {/* Mobile: accordion → cartes par permission */}
            <div className="md:hidden">
                <Accordion type="multiple" defaultValue={allThemes} className="space-y-2">
                    {groups.map((group) => {
                        const groupKey = `group:${group.theme}`;
                        return (
                            <AccordionItem
                                key={group.theme}
                                value={group.theme}
                                className="bg-surface rounded-xl border border-border overflow-hidden border-b"
                            >
                                <AccordionTrigger className="px-4 py-3 font-normal text-foreground hover:no-underline">
                                    {group.theme}
                                </AccordionTrigger>
                                <AccordionContent className="px-6 pb-4">
                                    {/* Tout cocher/décocher par rôle */}
                                    <div className="bg-muted/40 rounded-lg p-3 border border-border mb-3">
                                        <div className="text-xs text-muted-foreground font-semibold mb-2">
                                            {t('account:toutCocherDecocher')}
                                        </div>
                                        <div className="space-y-2">
                                            {roles.map((role) => {
                                                const isSaving = savingGroupKey === `${groupKey}:${role.apiRole}`;
                                                const allChecked = isGroupAllChecked(group, role.apiRole, matrix);
                                                return (
                                                    <label
                                                        key={role.key}
                                                        className="flex items-center justify-between cursor-pointer"
                                                    >
                                                        <span className="text-[13px] text-muted-foreground">
                                                            {role.label}
                                                        </span>
                                                        {isSaving ? (
                                                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                                        ) : (
                                                            <Checkbox
                                                                checked={allChecked}
                                                                onCheckedChange={() =>
                                                                    onGroupToggle(group, role, !allChecked)
                                                                }
                                                            />
                                                        )}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        {group.permissions.map((perm) => {
                                            const isDisabled = !perm.apiCode;
                                            return (
                                                <div
                                                    key={perm.id}
                                                    className={cn(
                                                        'bg-card rounded-lg p-3 border border-border',
                                                        isDisabled && 'opacity-50',
                                                    )}
                                                >
                                                    <div className="mb-3">
                                                        <div className="text-sm text-foreground font-medium">
                                                            {perm.label}
                                                        </div>
                                                        {perm.description && (
                                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                                {perm.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="space-y-3">
                                                        {roles.map((role) => {
                                                            const key = perm.apiCode
                                                                ? makeSavingKey(perm, role.apiRole)
                                                                : null;
                                                            const isSaving = key && savingKey === key;
                                                            return (
                                                                <label
                                                                    key={role.key}
                                                                    className={cn(
                                                                        'flex items-center justify-between',
                                                                        !isDisabled && 'cursor-pointer',
                                                                    )}
                                                                >
                                                                    <span className="text-[13px] text-muted-foreground">
                                                                        {role.label}
                                                                    </span>
                                                                    {isSaving ? (
                                                                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                                                    ) : (
                                                                        <Checkbox
                                                                            checked={getPermValue(
                                                                                perm,
                                                                                role.apiRole,
                                                                                matrix,
                                                                            )}
                                                                            disabled={isDisabled}
                                                                            onCheckedChange={
                                                                                !isDisabled
                                                                                    ? () => onToggle(perm, role)
                                                                                    : undefined
                                                                            }
                                                                        />
                                                                    )}
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </div>

            {/* Desktop: tableau avec accordion par groupe */}
            <div className="hidden md:block bg-card rounded-xl border border-border overflow-hidden">
                {/* En-tête fixe */}
                <div className="border-b border-border bg-muted">
                    <div className={cn('grid gap-3 px-5 py-3.5', desktopGridCols)}>
                        <div className="text-[13px] text-foreground font-semibold">{t('account:autorisation')}</div>
                        {roles.map((role) => (
                            <div key={role.key} className="text-[13px] text-foreground font-semibold text-center">
                                {role.label}
                            </div>
                        ))}
                        <div />
                    </div>
                </div>
                {/* Groupes */}
                <Accordion type="multiple" defaultValue={allThemes}>
                    {groups.map((group, groupIdx) => {
                        const groupKey = `group:${group.theme}`;
                        return (
                            <AccordionItem
                                key={group.theme}
                                value={group.theme}
                                className={cn(groupIdx < groups.length - 1 && 'border-b')}
                            >
                                <div
                                    className={cn(
                                        'grid gap-3 items-center bg-muted/30 hover:bg-muted/50 px-5',
                                        desktopGridCols,
                                    )}
                                >
                                    <AccordionPrimitive.Trigger className="py-3 text-sm text-foreground text-left cursor-pointer">
                                        {group.theme}
                                    </AccordionPrimitive.Trigger>
                                    {roles.map((role) => {
                                        const isSaving = savingGroupKey === `${groupKey}:${role.apiRole}`;
                                        const allChecked = isGroupAllChecked(group, role.apiRole, matrix);
                                        return (
                                            <div key={role.key} className="flex items-center justify-center">
                                                {isSaving ? (
                                                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                                ) : (
                                                    <Checkbox
                                                        checked={allChecked}
                                                        onCheckedChange={() => onGroupToggle(group, role, !allChecked)}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                    <AccordionPrimitive.Trigger className="flex items-center justify-center cursor-pointer [&[data-state=open]>svg]:rotate-180">
                                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                                    </AccordionPrimitive.Trigger>
                                </div>
                                <AccordionContent className="p-0">
                                    {group.permissions.map((perm, index) => {
                                        const isLastPerm = index === group.permissions.length - 1;
                                        const isDisabled = !perm.apiCode;
                                        return (
                                            <div
                                                key={perm.id}
                                                className={cn(
                                                    'grid gap-3 pl-10 pr-5 py-3.5 hover:bg-surface transition-colors',
                                                    desktopGridCols,
                                                    !isLastPerm && 'border-b border-border',
                                                    isDisabled && 'bg-muted opacity-50',
                                                )}
                                            >
                                                <div className="flex flex-col justify-center">
                                                    <span className="text-sm text-foreground">{perm.label}</span>
                                                    {perm.description && (
                                                        <span className="text-xs text-muted-foreground">
                                                            {perm.description}
                                                        </span>
                                                    )}
                                                </div>
                                                {roles.map((role) => {
                                                    const key = perm.apiCode ? makeSavingKey(perm, role.apiRole) : null;
                                                    const isSaving = key && savingKey === key;
                                                    return (
                                                        <div
                                                            key={role.key}
                                                            className="flex items-center justify-center"
                                                        >
                                                            {isSaving ? (
                                                                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                                            ) : (
                                                                <Checkbox
                                                                    checked={getPermValue(perm, role.apiRole, matrix)}
                                                                    disabled={isDisabled}
                                                                    onCheckedChange={
                                                                        !isDisabled
                                                                            ? () => onToggle(perm, role)
                                                                            : undefined
                                                                    }
                                                                    className={isDisabled ? 'cursor-not-allowed' : ''}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                <div />
                                            </div>
                                        );
                                    })}
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </div>
        </div>
    );
}

/* ─── Export principal ────────────────────────────────────── */

export default function DefaultPermissionsContent() {
    const { t } = useTranslation();
    const INDIVIDUAL_PERMISSION_GROUPS = getIndividualPermissionGroups();
    const COMPANY_PERMISSION_GROUPS = getCompanyPermissionGroups();
    const INDIVIDUAL_ROLE = getIndividualRole();
    const COMPANY_ROLES = getCompanyRoles();
    const ACCOUNT_TYPES = getAccountTypes();
    const [accountType, setAccountType] = useState('companies');
    const [isLoading, setIsLoading] = useState(true);
    const [matrix, setMatrix] = useState({});
    const [savingKey, setSavingKey] = useState(null);
    const [savingGroupKey, setSavingGroupKey] = useState(null);
    const matrixRef = useRef(matrix);

    useEffect(() => {
        matrixRef.current = matrix;
    }, [matrix]);

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                const res = await horodatyApi.managerGetPermissions();
                setMatrix(res.data?.data?.matrix ?? {});
            } catch {
                toast.error(t('account:erreurLorsDuChargementDesPermissions'));
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    const handleToggle = useCallback(async (perm, role) => {
        if (!perm.apiCode) return;
        const apiRole = role.apiRole;
        const scope = perm.apiScope ?? 'SELF';
        const key = makeSavingKey(perm, apiRole);
        const currentValue = matrixRef.current[apiRole]?.[perm.apiCode]?.[scope] ?? false;
        const newValue = !currentValue;

        // Mise à jour optimiste
        setMatrix((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            if (!next[apiRole]) next[apiRole] = {};
            if (!next[apiRole][perm.apiCode]) next[apiRole][perm.apiCode] = { SELF: false, DESCENDANTS: false };
            next[apiRole][perm.apiCode][scope] = newValue;
            return next;
        });

        setSavingKey(key);
        try {
            await horodatyApi.managerTogglePermission(apiRole, perm.apiCode, scope, newValue);
        } catch {
            toast.error(t('account:erreurLorsDeLaSauvegarde'));
            setMatrix((prev) => {
                const next = JSON.parse(JSON.stringify(prev));
                if (next[apiRole]?.[perm.apiCode]) next[apiRole][perm.apiCode][scope] = currentValue;
                return next;
            });
        } finally {
            setSavingKey(null);
        }
    }, [t]);

    const handleGroupToggle = useCallback(async (group, role, newValue) => {
        const apiRole = role.apiRole;
        const groupKey = `group:${group.theme}:${apiRole}`;

        // Collect all toggleable permissions in this group
        const permsToToggle = group.permissions
            .filter((p) => p.apiCode)
            .map((p) => {
                const scope = p.apiScope ?? 'SELF';
                return { permission: p.apiCode, scope, value: newValue };
            });

        if (permsToToggle.length === 0) return;

        // Snapshot for rollback
        const snapshot = JSON.parse(JSON.stringify(matrixRef.current));

        // Optimistic update
        setMatrix((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            if (!next[apiRole]) next[apiRole] = {};
            for (const p of permsToToggle) {
                if (!next[apiRole][p.permission]) next[apiRole][p.permission] = { SELF: false, DESCENDANTS: false };
                next[apiRole][p.permission][p.scope] = newValue;
            }
            return next;
        });

        setSavingGroupKey(groupKey);
        try {
            await horodatyApi.managerBatchTogglePermissions(apiRole, permsToToggle);
        } catch {
            toast.error(t('account:erreurLorsDeLaSauvegardeGroupee'));
            setMatrix(snapshot);
        } finally {
            setSavingGroupKey(null);
        }
    }, [t]);

    return (
        <div className="space-y-5 p-5 pb-20">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="bg-surface rounded-full h-9 w-9 flex items-center justify-center shrink-0">
                    <Shield className="h-5 w-5 text-foreground" />
                </div>
                <div>
                    <h3 className="text-base text-foreground font-semibold">{t('account:autorisationsSousComptes')}</h3>
                    <p className="text-[13px] text-muted-foreground mt-0.5">
                        {t('account:definissezLesPermissionsAppliqueesALaCreationDe')}
                    </p>
                </div>
            </div>

            {/* Sélecteur de type de compte */}
            <div className="bg-surface rounded-xl p-1 flex gap-1 w-full max-w-[320px]">
                {ACCOUNT_TYPES.map((tab) => (
                    <Button
                        key={tab.value}
                        variant="plain"
                        color="neutral"
                        size="xlg"
                        onClick={() => setAccountType(tab.value)}
                        className={cn(
                            'flex-1 text-[13px]',
                            accountType === tab.value
                                ? 'bg-card text-foreground shadow-sm hover:bg-card'
                                : 'text-muted-foreground',
                        )}
                    >
                        {tab.label}
                    </Button>
                ))}
            </div>

            {/* Contenu */}
            {isLoading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-secondary" />
                </div>
            ) : accountType === 'companies' ? (
                <GroupedPermissionsTable
                    groups={COMPANY_PERMISSION_GROUPS}
                    roles={COMPANY_ROLES}
                    matrix={matrix}
                    savingKey={savingKey}
                    onToggle={handleToggle}
                    onGroupToggle={handleGroupToggle}
                    savingGroupKey={savingGroupKey}
                />
            ) : (
                <GroupedPermissionsTable
                    groups={INDIVIDUAL_PERMISSION_GROUPS}
                    roles={INDIVIDUAL_ROLE}
                    matrix={matrix}
                    savingKey={savingKey}
                    onToggle={handleToggle}
                    onGroupToggle={handleGroupToggle}
                    savingGroupKey={savingGroupKey}
                />
            )}
        </div>
    );
}
